<template>
    <div>
        <v-toolbar flat>
            <v-toolbar-title>
                <slot name="title"></slot>

                <div class="text-overline my-2" v-if="serviceSubmissionCutoffDate">
                    <v-chip label outlined>
                        Submissions {{ submissionCutoffDateIsPassed ? 'Closed' : 'Close' }}:
                        {{ $moment.utc(serviceSubmissionCutoffDate).local().format('l') }}
                    </v-chip>
                </div>
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <template v-if="selectedRows.length">
                Selected <span class="selected-count mx-2">{{ selectedRows.length }}</span
                >:
                <div class="button-grid ml-2">
                    <v-btn v-if="allowModeration" small color="#1cc78e" dark @click="bulkAction('approve')"
                        >Approve</v-btn
                    >
                    <v-btn v-if="allowModeration" small color="error" class="text-white" @click="bulkAction('deny')"
                        >Deny</v-btn
                    >
                    <v-btn small text color="error" class="text-error" @click="bulkDeleteModal = true">
                        <font-awesome-icon icon="fa-regular fa-trash-can" />
                    </v-btn>
                </div>
            </template>
            <template v-else-if="allowModeration">
                <v-btn v-if="totalUnknown > 0" color="primary" @click="moderate">Moderate</v-btn>
                <span v-else class="text-overline"
                    >Moderation Complete
                    <font-awesome-icon icon="fa-solid fa-circle-check" style="color: #1cc78e" />
                </span>
            </template>
            <v-spacer v-if="!selectedRows.length && !allowModeration"></v-spacer>
            <v-btn color="primary" text @click="exportToCSV" :loading="exporting" :disabled="exporting">
                <font-awesome-icon icon="fa-solid fa-download" class="mr-1" />
                Export
            </v-btn>
        </v-toolbar>
        <PaginatedTableBase
            ref="tableRef"
            :headers="headers"
            :fetch-args="fetchArgs"
            :fetch-function="fetchGuestbook"
            @click:row="showMessage"
            :show-select="true"
            @change:selection="selection => (selectedRows = selection)"
        >
            <template v-if="mode === 'admin' || mode === 'owner'" #header.moderatedStatus="{ header }">
                <v-menu @input="handleMenuToggle" :close-on-content-click="false" :offset-y="true">
                    <template v-slot:activator="{ on: menu, attrs }">
                        <custom-tooltip :tooltipProps="{ top: true }">
                            <template v-slot:activator>
                                <span v-bind="attrs" v-on="{ ...menu }">
                                    {{ header.text }} ({{ selectedStatuses.length }})
                                    <font-awesome-icon icon="fa-solid fa-chevron-down" />
                                </span>
                            </template>

                            <template v-slot:content>
                                <span>Select Filters</span>
                            </template>
                        </custom-tooltip>
                    </template>
                    <v-list style="width: 200px">
                        <v-list-item v-for="(item, index) in guestbookStatusLabels" :key="index" style="height: 20px">
                            <v-checkbox v-model="selectedStatuses" :label="item.label" :value="item.value"></v-checkbox>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </template>

            <template v-if="mode === 'admin'" #header.ownerName="{ header }">
                <v-menu @input="handleMenuToggle" :close-on-content-click="false" :offset-y="true">
                    <template v-slot:activator="{ on: menu, attrs }">
                        <custom-tooltip :tooltipProps="{ top: true }">
                            <template v-slot:activator>
                                <span v-bind="attrs" v-on="{ ...menu }">
                                    {{ header.text }}
                                </span>
                            </template>

                            <template v-slot:content>
                                <span>Select Filters</span>
                            </template>
                        </custom-tooltip>
                    </template>

                    <v-list style="width: 300px">
                        <v-list-item>
                            <v-select
                                v-model="selectedOwner"
                                :items="ownerOptions"
                                item-text="name"
                                item-value="id"
                                label="Select Owner"
                            >
                            </v-select>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </template>

            <template v-if="mode === 'admin' || mode === 'owner'" #header.optIn="{ header }">
                <v-menu @input="handleMenuToggle" :close-on-content-click="false" :offset-y="true">
                    <template v-slot:activator="{ on: menu, attrs }">
                        <custom-tooltip :tooltipProps="{ top: true }">
                            <template v-slot:activator>
                                <span v-bind="attrs" v-on="{ ...menu }">
                                    {{ header.text }}
                                    <font-awesome-icon icon="fa-solid fa-chevron-down" />
                                </span>
                            </template>

                            <template v-slot:content>
                                <span>Select Filters</span>
                            </template>
                        </custom-tooltip>
                    </template>
                    <v-list style="width: 200px">
                        <v-list-item>
                            <v-radio-group dense v-model="radioGroupOptin">
                                <v-radio
                                    class="mb-2"
                                    v-for="item in radioOptions"
                                    :key="item.label"
                                    :label="item.label"
                                    :value="item.value"
                                ></v-radio>
                            </v-radio-group>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </template>

            <template v-if="mode === 'admin' || mode === 'owner'" #header.inMarket="{ header }">
                <v-menu @input="handleMenuToggle" :close-on-content-click="false" :offset-y="true">
                    <template v-slot:activator="{ on: menu, attrs }">
                        <custom-tooltip :tooltipProps="{ top: true }">
                            <template v-slot:activator>
                                <span v-bind="attrs" v-on="{ ...menu }">
                                    {{ header.text }}
                                    <font-awesome-icon icon="fa-solid fa-chevron-down" />
                                </span>
                            </template>

                            <template v-slot:content>
                                <span>Select Filters</span>
                            </template>
                        </custom-tooltip>
                    </template>
                    <v-list style="width: 200px">
                        <v-list-item>
                            <v-radio-group dense v-model="radioGroupInMarket">
                                <v-radio
                                    class="mb-2"
                                    v-for="item in radioOptions"
                                    :key="item.label"
                                    :label="item.label"
                                    :value="item.value"
                                ></v-radio>
                            </v-radio-group>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </template>

            <template v-if="mode === 'admin' || mode === 'owner'" #header.isLive="{ header }">
                <v-menu @input="handleMenuToggle" :close-on-content-click="false" :offset-y="true">
                    <template v-slot:activator="{ on: menu, attrs }">
                        <custom-tooltip :tooltipProps="{ top: true }">
                            <template v-slot:activator>
                                <span v-bind="attrs" v-on="{ ...menu }">
                                    {{ header.text }}
                                    <font-awesome-icon icon="fa-solid fa-chevron-down" />
                                </span>
                            </template>

                            <template v-slot:content>
                                <span>Select Filters</span>
                            </template>
                        </custom-tooltip>
                    </template>
                    <v-list style="width: 200px">
                        <v-list-item>
                            <v-radio-group dense v-model="radioGroupLive">
                                <v-radio
                                    class="mb-2"
                                    v-for="item in radioOptions"
                                    :key="item.label"
                                    :label="item.label"
                                    :value="item.value"
                                ></v-radio>
                            </v-radio-group>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </template>

            <template #item.createDate="{ item }">
                <div class="text-caption text-center">{{ $moment.utc(item.createDate).local().format('LT') }}</div>
                <div class="text-center">{{ $moment.utc(item.createDate).local().format('l') }}</div>
            </template>

            <template #item.email="{ item }">
                <div class="contact-name">
                    <template v-if="item.fullName">{{ item.fullName }}</template>
                    <template v-else-if="item.firstName || item.lastName">{{
                        `${item.firstName ? item.firstName : ''} ${item.lastName ? item.lastName : ''}`
                    }}</template>
                    <template v-else>Anonymous</template>
                </div>
                <div v-if="item.email">
                    {{ item.email }}
                </div>
            </template>
            <!-- <template #item.fullName="{ item }">
                <template v-if="item.fullName">{{ item.fullName }}</template>
                <template v-else-if="item.firstName || item.lastName">{{
                    `${item.firstName ? item.firstName : ''} ${item.lastName ? item.lastName : ''}`
                }}</template>
                <template v-else>Anonymous</template>
            </template> -->
            <template #item.serviceName="{ item }">
                <div v-if="item.serviceSlug">
                    <a @click.stop :href="`/services/${item.serviceSlug}`" target="_blank">
                        {{ item.serviceFirstName }} {{ item.serviceLastName }}
                    </a>
                </div>
            </template>
            <template #item.funeralHomeName="{ item }">
                <span>{{ item.funeralHomeName }}</span>
            </template>

            <template #item.moderatedStatus="{ item }">
                <div class="d-flex justify-content-center" v-if="item.moderatedStatus !== null">
                    <!-- <div :class="['moderation-status', statusDisplayMap[item.moderatedStatus] ? statusDisplayMap[item.moderatedStatus].toLowerCase() : '']">
                        {{ statusDisplayMap[item.moderatedStatus] }}
                    </div> -->
                    <v-chip small dark :color="getModeratedStatusColor(item.moderatedStatus)">
                        {{ statusDisplayMap[item.moderatedStatus] }}
                    </v-chip>
                </div>
            </template>
            <template #item.message="{ item }">
                <template v-if="item.message && item.message.length">
                    <div class="message-preview">{{ item.message }}</div>
                </template>
            </template>
            <template #item.optIn="{ item }">
                <div class="d-flex justify-content-center">
                    <!-- <v-checkbox v-model="item.optIn" readonly></v-checkbox> -->
                    <font-awesome-icon
                        v-if="item.optIn"
                        icon="fa-solid fa-circle-check"
                        style="color: #12b77f; font-size: 1.1rem"
                    ></font-awesome-icon>

                    <font-awesome-icon
                        v-else
                        icon="fa-solid fa-circle-xmark"
                        style="color: #ff5252; font-size: 1.1rem"
                    ></font-awesome-icon>
                </div>
            </template>

            <template #item.inMarket="{ item }">
                <div class="d-flex justify-content-center">
                    <!-- <v-checkbox v-model="item.optIn" readonly></v-checkbox> -->
                    <font-awesome-icon
                        v-if="item.inMarket"
                        icon="fa-solid fa-circle-check"
                        style="color: #12b77f; font-size: 1.1rem"
                    ></font-awesome-icon>

                    <font-awesome-icon
                        v-else
                        icon="fa-solid fa-circle-xmark"
                        style="color: #ff5252; font-size: 1.1rem"
                    ></font-awesome-icon>
                </div>
            </template>

            <template #item.isLive="{ item }">
                <div class="d-flex justify-content-center">
                    <font-awesome-icon
                        v-if="item.isLive"
                        icon="fa-solid fa-circle-check"
                        style="color: #12b77f; font-size: 1.1rem"
                    ></font-awesome-icon>

                    <font-awesome-icon
                        v-else
                        icon="fa-solid fa-circle-xmark"
                        style="color: #ff5252; font-size: 1.1rem"
                    ></font-awesome-icon>
                </div>
            </template>

            <template #item.actions="{ item }">
                <div class="d-flex justify-center align-center">
                    <CustomTooltip :disable="!item.message" :tooltipProps="{ top: true }">
                        <template #activator>
                            <v-btn :disabled="!item.message" @click.stop="showMessage(item)" small text>
                                <font-awesome-icon icon="fa-solid fa-message"></font-awesome-icon>
                            </v-btn>
                        </template>

                        <template #content>
                            <span>Show Message</span>
                        </template>
                    </CustomTooltip>

                    <CustomTooltip v-if="$auth.role.includes('SuperAdmin')" :tooltipProps="{ top: true }">
                        <template #activator>
                            <v-btn @click.stop="showDetails(item)" small text>
                                <font-awesome-icon icon="fa-solid fa-bars"></font-awesome-icon>
                            </v-btn>
                        </template>

                        <template #content>
                            <span>Details</span>
                        </template>
                    </CustomTooltip>

                    <CustomTooltip :tooltipProps="{ top: true }">
                        <template #activator>
                            <v-btn color="error" @click.stop="openDeleteModal(item)" small text>
                                <font-awesome-icon icon="fa-solid fa-trash"></font-awesome-icon>
                            </v-btn>
                        </template>

                        <template #content>
                            <span>Delete Message</span>
                        </template>
                    </CustomTooltip>
                </div>
            </template>
        </PaginatedTableBase>

        <v-dialog @click:outside="hideMessage" v-model="messageModal" max-width="600px">
            <v-card v-if="selectedResponse" :key="selectedResponse.id">
                <v-card-title>
                    Message
                    <div class="ml-2">
                        <v-chip dark :color="getModeratedStatusColor(selectedResponse.moderatedStatus)">
                            {{ statusDisplayMap[selectedResponse.moderatedStatus] }}
                        </v-chip>
                    </div>
                    <v-spacer />
                    <div
                        class="button-grid"
                        v-if="selectedResponse.moderatedStatus === gbStatuses.UNKNOWN && allowModeration"
                    >
                        <v-btn depressed @click.stop="prevItem()" small :disabled="!totalUnknown || tableLoading">
                            <font-awesome-icon icon="fa-solid fa-arrow-left"></font-awesome-icon>
                        </v-btn>
                        <div class="tag py-0">
                            <!-- <template v-if="!tableLoading"
                                >{{ displaySelectedResponseIndex + 1 }} / {{ total }}</template
                            > -->
                            <template v-if="!tableLoading">{{ responseIndex + 1 }} / {{ totalUnknown }}</template>
                            <template v-else>...</template>
                        </div>
                        <v-btn depressed @click.stop="nextItem()" small :disabled="!totalUnknown || tableLoading">
                            <font-awesome-icon icon="fa-solid fa-arrow-right"></font-awesome-icon>
                        </v-btn>
                    </div>
                </v-card-title>

                <v-card-text v-if="selectedResponse" class="pb-2">
                    <div class="signer-details">
                        <div>
                            <div class="font-weight-bold">Signer</div>
                            <div>
                                <template v-if="selectedResponse.fullName">{{ selectedResponse.fullName }}</template>
                                <template v-else-if="selectedResponse.firstName || selectedResponse.lastName">{{
                                    `${selectedResponse.firstName ? selectedResponse.firstName : ''} ${
                                        selectedResponse.lastName ? selectedResponse.lastName : ''
                                    }`
                                }}</template>
                                <template v-else>Anonymous</template>
                            </div>
                        </div>
                        <div v-if="selectedResponse.email">
                            <div class="font-weight-bold">Email</div>
                            <div>
                                <template>{{ selectedResponse.email }}</template>
                            </div>
                        </div>
                        <div v-if="selectedResponse.userCity || selectedResponse.userState">
                            <div class="font-weight-bold">Location</div>
                            <div>
                                <span v-if="selectedResponse.userCity"
                                    >{{ selectedResponse.userCity }}{{ selectedResponse.userState ? ', ' : '' }}</span
                                >
                                <span v-if="selectedResponse.userState">{{ selectedResponse.userState }}</span>
                            </div>
                        </div>
                    </div>
                </v-card-text>
                <v-card-text>
                    <div v-if="selectedResponse">
                        <div class="d-flex justify-space-between font-weight-bold">
                            Message
                            <div class="mb-1">
                                <v-btn @click.stop="editMessageConfirmationModal = true" small text>
                                    <font-awesome-icon icon="fa-solid fa-pen-to-square"></font-awesome-icon>
                                </v-btn>
                                <v-btn color="error" @click.stop="openDeleteModal(selectedResponse)" small text>
                                    <font-awesome-icon icon="fa-solid fa-trash"></font-awesome-icon>
                                </v-btn>
                            </div>
                        </div>
                        <p class="message-box">
                            {{ selectedResponse.message }}
                        </p>
                    </div>

                    <p v-else>No Message to display</p>
                </v-card-text>

                <v-card-actions>
                    <v-btn depressed @click="hideMessage">Close</v-btn>
                    <v-spacer></v-spacer>
                    <div v-if="allowModeration" class="button-grid">
                        <v-btn
                            color="error"
                            @click="denyItem(selectedResponse)"
                            :disabled="
                                [statusMap.SPAM, statusMap.OFFENSIVE, statusMap.QUESTIONABLE].includes(
                                    selectedResponse.moderatedStatus,
                                )
                            "
                            >{{
                                [statusMap.SPAM, statusMap.OFFENSIVE, statusMap.QUESTIONABLE].includes(
                                    selectedResponse.moderatedStatus,
                                )
                                    ? 'Denied'
                                    : 'Deny'
                            }}</v-btn
                        >
                        <v-btn @click.stop="nextItem()" :disabled="displaySelectedResponseIndex + 1 === total">
                            Skip
                        </v-btn>
                        <v-btn
                            color="#1cc78e"
                            :dark="selectedResponse.moderatedStatus !== statusMap.SAFE"
                            @click="approveItem(selectedResponse)"
                            :disabled="selectedResponse.moderatedStatus === statusMap.SAFE"
                        >
                            {{ selectedResponse.moderatedStatus === statusMap.SAFE ? 'Approved' : 'Approve' }}</v-btn
                        >
                    </div>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="editMessageConfirmationModal" max-width="550px">
            <v-card>
                <v-card-title>Edit Guest Entry?</v-card-title>
                <v-card-text>
                    <p>Please remember:</p>
                    <ul>
                        <li>Minor corrections (like fixing obvious typos) are okay</li>
                        <li>Do not alter the original meaning or sentiment of the message</li>
                        <li>
                            For offensive content or inappropriate entries, consider removing the entry entirely instead
                            of editing
                        </li>
                    </ul>

                    <!-- <v-textarea outlined v-model="tempMessage" label="Message"></v-textarea> -->
                    <strong>Are you sure you want to edit this guest message?</strong>
                </v-card-text>

                <v-card-actions class="d-flex justify-space-between">
                    <v-btn @click="cancelEditMessage" depressed>Cancel</v-btn>
                    <v-btn @click="openEditMessageModal(selectedResponse)" depressed>Proceed with edit</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="editMessageModal" max-width="550px">
            <v-card>
                <v-card-title>Edit Guest Entry</v-card-title>
                <v-card-text>
                    <v-textarea outlined v-model="tempMessage"></v-textarea>
                </v-card-text>

                <v-card-actions class="d-flex justify-space-between">
                    <v-btn :disabled="loading" @click="cancelEditMessage" depressed>Cancel</v-btn>
                    <v-btn :disabled="loading" :loading="loading" @click="saveEditMessage" depressed>Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog @click:outside="hideDetails" v-model="detailsModal" max-width="800px">
            <v-card>
                <v-card-title> Details </v-card-title>

                <v-card-text v-if="selectedResponse">
                    <v-row>
                        <v-col cols="6">
                            <v-text-field label="Id" :value="selectedResponse.id"></v-text-field>
                        </v-col>

                        <v-col cols="6">
                            <v-text-field
                                readonly
                                label="Service Id"
                                :value="selectedResponse.serviceId"
                            ></v-text-field>
                        </v-col>

                        <v-col cols="6">
                            <v-text-field
                                readonly
                                label="Funeral Home Id"
                                :value="selectedResponse.funeralHomeId"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field readonly label="Owner Id" :value="selectedResponse.ownerId"></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                readonly
                                label="External Id"
                                :value="selectedResponse.externalId"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field readonly label="Email" :value="selectedResponse.email"></v-text-field>
                        </v-col>

                        <v-col cols="6">
                            <v-text-field label="Config URL" :value="selectedResponse.configURL"></v-text-field>
                        </v-col>

                        <v-col cols="6">
                            <v-text-field readonly label="Full Name" :value="selectedResponse.fullName"></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                readonly
                                label="First Name"
                                :value="selectedResponse.firstName"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field readonly label="Last Name" :value="selectedResponse.lastName"></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field readonly label="Phone" :value="selectedResponse.phone"></v-text-field>
                        </v-col>

                        <v-col cols="6">
                            <v-text-field
                                readonly
                                label="Ip Address"
                                :value="selectedResponse.ipAddress"
                            ></v-text-field>
                        </v-col>

                        <v-col cols="6">
                            <v-text-field readonly label="Latitude" :value="selectedResponse.latitude"></v-text-field>
                        </v-col>

                        <v-col cols="6">
                            <v-text-field readonly label="Longitude" :value="selectedResponse.longitude"></v-text-field>
                        </v-col>

                        <v-col cols="6">
                            <v-text-field readonly label="City" :value="selectedResponse.city"></v-text-field>
                        </v-col>

                        <v-col cols="6">
                            <v-text-field
                                readonly
                                label="Country Code"
                                :value="selectedResponse.countryCode"
                            ></v-text-field>
                        </v-col>

                        <v-col cols="6">
                            <v-text-field
                                readonly
                                label="Metro Code"
                                :value="selectedResponse.metroCode"
                            ></v-text-field>
                        </v-col>

                        <v-col cols="6">
                            <v-text-field
                                readonly
                                label="Region Code"
                                :value="selectedResponse.regionCode"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                readonly
                                label="Region Name"
                                :value="selectedResponse.regionName"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field readonly label="Time Zone" :value="selectedResponse.timeZone"></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                readonly
                                label="Create Date"
                                :value="selectedResponse.createDate"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field readonly label="Opt In" :value="selectedResponse.optIn"></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                readonly
                                label="User Address 1"
                                :value="selectedResponse.userAddress1"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                readonly
                                label="User Address 2"
                                :value="selectedResponse.userAddress2"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field readonly label="User City" :value="selectedResponse.userCity"></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                readonly
                                label="User State"
                                :value="selectedResponse.userState"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field readonly label="User Zip" :value="selectedResponse.userZip"></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                readonly
                                label="User Country"
                                :value="selectedResponse.userCountry"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                readonly
                                label="Relationship"
                                :value="selectedResponse.relationship"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field
                                label="Moderated Status"
                                :value="selectedResponse.moderatedStatus"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="6">
                            <v-text-field label="In Market" :value="selectedResponse.inMarket"></v-text-field>
                        </v-col>
                        <v-col cols="12">
                            <v-textarea label="Custom Fields" :value="selectedResponse.customFields"></v-textarea>
                        </v-col>

                        <!-- <v-col cols="6">
                            <v-text-field label="Custom Fields" :value="selectedResponse.customFields"></v-text-field>
                        </v-col> -->
                    </v-row>
                </v-card-text>

                <v-card-actions>
                    <v-btn depressed @click="hideDetails">Close</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog @click:outside="bulkDeleteModal = false" v-model="bulkDeleteModal" max-width="600px">
            <v-card>
                <v-card-title> Delete Response </v-card-title>
                <v-card-text v-if="selectedRows && selectedRows.length" class="pb-2">
                    <p>
                        Please confirm you would like to delete {{ selectedRows.length }}
                        {{ selectedRows.length === 1 ? 'entry' : 'entries' }}.
                    </p>
                    <p>This cannot be undone.</p>
                </v-card-text>
                <v-card-actions class="d-flex justify-space-between">
                    <v-btn depressed @click="bulkDeleteModal = false">Close</v-btn>
                    <v-btn depressed color="error" @click="deleteSelection">Delete</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog @click:outside="closeDeleteModal" v-model="deleteModal" max-width="600px">
            <v-card>
                <v-card-title> Are You Sure? </v-card-title>

                <v-card-text v-if="selectedResponse" class="pb-2">
                    <div class="signer-details">
                        <div>
                            <div class="font-weight-bold">Signer</div>
                            <div>
                                <template v-if="selectedResponse.fullName">{{ selectedResponse.fullName }}</template>
                                <template v-else-if="selectedResponse.firstName || selectedResponse.lastName">{{
                                    `${selectedResponse.firstName ? selectedResponse.firstName : ''} ${
                                        selectedResponse.lastName ? selectedResponse.lastName : ''
                                    }`
                                }}</template>
                                <template v-else>Anonymous</template>
                            </div>
                        </div>
                        <div v-if="selectedResponse.email">
                            <div class="font-weight-bold">Email</div>
                            <div>
                                <template>{{ selectedResponse.email }}</template>
                            </div>
                        </div>
                        <div v-if="selectedResponse.userCity || selectedResponse.userState">
                            <div class="font-weight-bold">Location</div>
                            <div>
                                <span v-if="selectedResponse.userCity"
                                    >{{ selectedResponse.userCity }}{{ selectedResponse.userState ? ', ' : '' }}</span
                                >
                                <span v-if="selectedResponse.userState">{{ selectedResponse.userState }}</span>
                            </div>
                        </div>
                    </div>
                </v-card-text>
                <v-card-text v-if="selectedResponse">
                    <div>
                        <div class="font-weight-bold">Message</div>
                        <p class="message-box" v-if="selectedResponse.message">
                            {{ selectedResponse.message }}
                        </p>
                        <p v-else>No Message to display</p>
                    </div>
                </v-card-text>

                <v-card-actions class="d-flex justify-space-between">
                    <v-btn depressed @click="closeDeleteModal">Close</v-btn>
                    <v-btn depressed color="error" @click="deleteResponse(selectedResponse)"
                        >Yes, Delete This Response</v-btn
                    >
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import PaginatedTableBase from '@/components/Tables/PaginatedTableBase.vue';
import GuestbookService from '@/services/guestbook.service';
import CustomTooltip from '@/components/ui/CustomTooltip.vue';
import { mapActions } from 'pinia';
import { findIndex, each } from 'lodash';
import { GUESTBOOK_RESPONSE_STATUSES, guestbookStatusLabels } from '@/constants';
import { debounce } from 'lodash';
import axios from 'axios';
import { useGlobalState } from '@/stores/globalState';

export default {
    components: { PaginatedTableBase, CustomTooltip },
    props: {
        serviceId: {
            type: Number,
            required: false,
        },
        funeralHomeId: {
            type: Number,
            required: false,
        },
        start: {
            type: String,
            required: false,
        },
        end: {
            type: String,
            required: false,
        },
        search: {
            type: String,
            required: false,
        },
        mode: {
            type: String,
            required: true,
            validator: value => ['funeralHome', 'service', 'admin', 'owner'].includes(value),
        },
    },
    data() {
        return {
            gbStatuses: GUESTBOOK_RESPONSE_STATUSES,
            guestbookStatusLabels,
            serviceSubmissionCutoffDate: null,
            messageModal: false,
            editMessageModal: false,
            loading: false,
            exporting: false,
            editMessageConfirmationModal: false,
            tempMessage: '',
            detailsModal: false,
            deleteModal: false,
            bulkDeleteModal: false,
            selectedResponse: null,
            tableLoading: false,
            selectedRows: [],
            total: 0,
            totalUnknown: 0,
            responseIndex: 0,
            currentUnknownStatusResponse: null,
            statusMap: GUESTBOOK_RESPONSE_STATUSES,
            statusDisplayMap: {
                0: 'Unknown',
                1: 'Safe',
                2: 'Spam',
                3: 'Denied',
                4: 'Questionable',
            },
            selectedStatuses: [0, 1, 2, 3, 4],
            statusMenuOpen: false,
            radioGroupLive: null,
            radioGroupInMarket: null,
            radioGroupOptin: null,
            radioOptions: [
                { label: 'Any', value: null },
                { label: 'True', value: true },
                { label: 'False', value: false },
            ],
            selectedOwner: null,
            owners: [],
        };
    },
    computed: {
        ownerOptions() {
            return [{ id: null, name: 'All' }, ...this.owners];
        },
        allowModeration() {
            return this.mode !== 'admin';
        },
        headers() {
            if (this.mode === 'funeralHome') {
                return [
                    { text: 'Date', value: 'createDate', align: 'center' },
                    { text: 'Service', value: 'serviceName' },
                    { text: 'Contact', value: 'email' },
                    { text: 'Message', value: 'message', sortable: false },
                    { text: 'Moderated', value: 'moderatedStatus' },
                    { text: 'Opt In', value: 'optIn', sortable: false },
                    { text: 'In Market', value: 'inMarket' },
                    // { text: 'Live', value: 'isLive' },
                    { text: 'Actions', value: 'actions', sortable: false },
                ];
            }

            if (this.mode === 'service') {
                return [
                    { text: 'Date', value: 'createDate', align: 'center' },
                    { text: 'Contact', value: 'email' },
                    { text: 'Message', value: 'message', sortable: false },
                    { text: 'Moderated', value: 'moderatedStatus' },
                    { text: 'Opt In', value: 'optIn' },
                    { text: 'In Market', value: 'inMarket' },
                    // { text: 'Live', value: 'isLive' },
                    { text: 'Actions', value: 'actions', sortable: false },
                ];
            }

            if (this.mode === 'admin' || this.mode === 'owner') {
                return [
                    { text: 'Date', value: 'createDate', align: 'center' },
                    { text: 'Owner', value: 'ownerName', sortable: false },
                    { text: 'Home', value: 'funeralHomeName' },
                    { text: 'Service', value: 'serviceName' },
                    { text: 'Contact', value: 'email' },
                    { text: 'Message', value: 'message', sortable: false },
                    { text: 'Moderated', value: 'moderatedStatus' },
                    { text: 'Opt In', value: 'optIn' },
                    { text: 'In Market', value: 'inMarket' },
                    { text: 'Live', value: 'isLive' },
                    { text: 'Actions', value: 'actions', sortable: false },
                ];
            }

            return [];
        },
        submissionCutoffDateIsPassed() {
            if (!this.serviceSubmissionCutoffDate) return false;

            const cutoffDate = new Date(this.serviceSubmissionCutoffDate);
            const now = new Date();

            if (cutoffDate <= now) {
                return true;
            }

            return false;
        },

        fetchArgs() {
            if (this.mode === 'funeralHome') {
                return { funeralHomeId: this.funeralHomeId, start: this.start, end: this.end };
            }
            if (this.mode === 'service') {
                return { serviceId: this.serviceId, start: this.start, end: this.end };
            }

            if (this.mode === 'admin' || 'owner') {
                let args = {
                    start: this.start,
                    end: this.end,
                    search: this.search,
                    status: this.selectedStatuses,
                    optIn: this.radioGroupOptin,
                    inMarket: this.radioGroupInMarket,
                    live: this.radioGroupLive,
                };

                if (this.mode === 'admin') {
                    args.ownerId = this.selectedOwner ?? null;
                }

                return args;
            }
            return {};
        },
        displaySelectedResponseIndex() {
            // This is the current item index for display
            // takes into account the pageSize, current Page and current Index;
            if (this.selectedResponse?.id) {
                const table = this.$refs.tableRef;
                const currentPage = table.options.page - 1;
                return this.pageSize * currentPage + this.selectedResponseIndex;
            }
            return -1;
        },
        selectedResponseIndex() {
            // Results in the table are paginated so this could be 0-9 for a pageSize of 10;
            if (this.selectedResponse?.id) {
                const table = this.$refs.tableRef;
                return findIndex(table.items, row => row.id === this.selectedResponse.id);
            }
            return -1;
        },
        pageSize() {
            const table = this.$refs.tableRef;
            return table.options.itemsPerPage || 10;
        },
    },
    watch: {
        search() {
            this.debouncedRefetchData();
        },
        start(newVal) {
            this.debouncedRefetchData();
        },
        end() {
            this.debouncedRefetchData();
        },
    },
    methods: {
        ...mapActions(useGlobalState, ['showSnackbar']),
        getModeratedStatusColor(value) {
            if (value === GUESTBOOK_RESPONSE_STATUSES.SAFE) {
                return '#1cc78e';
            } else if (value === GUESTBOOK_RESPONSE_STATUSES.UNKNOWN) {
                return '#000000';
            } else {
                return '#f96060';
            }
        },
        handleMenuToggle() {
            if (this.statusMenuOpen == false) {
                this.statusMenuOpen = true;
            } else if (this.statusMenuOpen == true) {
                this.statusMenuOpen = false;
                this.debouncedRefetchData();
            }
        },
        async fetchGuestbook(params) {
            if (this.mode === 'funeralHome') {
                if (!params.funeralHomeId) throw new Error('funeralHomeId is required');
                const response = await this.api.getResponsesByFuneralHome(params.funeralHomeId, params);

                this.total = response.data.total;
                this.$emit('totalEntries', response.data.total);

                return response.data;
            }

            if (this.mode === 'service') {
                if (!params.serviceId) throw new Error('serviceId is required');
                const response = await this.api.getResponsesByService(params.serviceId, params);

                this.total = response.data.total;
                this.$emit('totalEntries', response.data.total);

                return response.data;
            }

            if (this.mode === 'owner') {
                const response = await this.api.getResponsesOwner(params);

                this.total = response.data.total;
                this.$emit('totalEntries', response.data.total);

                return response.data;
            }

            if (this.mode === 'admin') {
                const response = await this.api.getResponsesAdmin(params);

                this.total = response.data.total;
                this.$emit('totalEntries', response.data.total);

                return response.data;
            }

            throw new Error('Invalid mode');
        },
        showMessage(response) {
            this.selectedResponse = response;
            this.messageModal = true;
        },
        hideMessage() {
            this.messageModal = false;
            this.selectedResponse = null;
        },
        showDetails(response) {
            if (!this.$auth.role.includes('SuperAdmin')) {
                return;
            }

            this.selectedResponse = response;
            this.detailsModal = true;
        },
        hideDetails() {
            this.detailsModal = false;
            this.selectedResponse = null;
        },

        async deleteResponse(response) {
            const delResponse = await this.api.deleteResponse(response.id);

            this.closeDeleteModal();
            this.hideMessage();
            this.showSnackbar({ message: 'Response deleted' });
            this.refetchTableData();
        },
        refetchTableData() {
            if (this.$refs.tableRef) {
                this.$refs.tableRef.fetchData();
                this.$emit('refresh');
            }
        },
        openEditMessageModal(response) {
            this.editMessageConfirmationModal = false;
            this.tempMessage = response.message;
            this.editMessageModal = true;
        },

        cancelEditMessage() {
            this.editMessageConfirmationModal = false;
            this.editMessageModal = false;
            this.tempMessage = '';
        },
        async saveEditMessage() {
            const responseId = this.selectedResponse.id;
            const message = this.tempMessage;
            try {
                this.loading = true;
                const resp = await this.api.updateGuestbookResponseMessage(responseId, message);

                this.selectedResponse.message = message;
                this.showSnackbar({ message: 'Message updated' });
                this.cancelEditMessage();
            } catch (error) {
                console.log('error saving response message', error);
            } finally {
                this.loading = false;
            }
        },
        openDeleteModal(response) {
            this.selectedResponse = response;
            this.deleteModal = true;
        },
        closeDeleteModal() {
            this.deleteModal = false;
        },
        async moderate() {
            const data = await this.getNextUnknown(false);

            this.totalUnknown = data.totalUnknown;

            this.currentUnknownStatusResponse = data.response;

            if (!this.currentUnknownStatusResponse) {
                this.responseIndex = 0;
                this.showSnackbar('No items to moderate');
                return;
            }

            this.responseIndex = data.responseIndex;

            this.showMessage(this.currentUnknownStatusResponse);
        },

        async deleteSelection() {
            this.bulkDeleteModal = false;
            each(this.selectedRows, async row => {
                await this.deleteResponse(row);
            });
            await this.$nextTick();
            this.clearSelection();
            this.refetchTableData();
        },
        clearSelection() {
            const table = this.$refs.tableRef;
            table.selection = [];
        },
        async prevItem() {
            let startAfter = null;
            if (this.selectedResponse?.id) {
                startAfter = this.selectedResponse.id;
            }
            const data = await this.getNextUnknown(true, startAfter);

            this.totalUnknown = data.totalUnknown;
            this.currentUnknownStatusResponse = data.response;

            if (this.currentUnknownStatusResponse) {
                this.responseIndex = data.responseIndex;
                this.showMessage(this.currentUnknownStatusResponse);
            } else {
                this.hideMessage();
                this.responseIndex = 0;
            }
        },
        async nextItem() {
            let startAfter = null;
            if (this.selectedResponse?.id) {
                startAfter = this.selectedResponse.id;
            }
            const data = await this.getNextUnknown(false, startAfter);

            this.totalUnknown = data.totalUnknown;
            this.currentUnknownStatusResponse = data.response;

            if (this.currentUnknownStatusResponse) {
                this.responseIndex = data.responseIndex;
                this.showMessage(this.currentUnknownStatusResponse);
            } else {
                this.hideMessage();
                this.responseIndex = 0;
            }
        },
        async getNextUnknown(orderByAsc, startAfterId = null) {
            try {
                if (this.mode == 'service') {
                    if (!this.serviceId) throw new Error('Invalid ServiceId');

                    const resp = await this.api.getNextUnknownByService(this.serviceId, orderByAsc, startAfterId);

                    return resp.data;
                } else if (this.mode === 'funeralHome') {
                    if (!this.funeralHomeId) throw new Error('Invalid FuneralHomeId');

                    const resp = await this.api.getNextUnknownByFuneralHome(
                        this.funeralHomeId,
                        orderByAsc,
                        startAfterId,
                    );

                    return resp.data;
                } else if (this.mode === 'owner') {
                    const ownerId = this?.$auth?.user['https://memoryshare.com/owner_id'];

                    if (!ownerId || ownerId <= 0) {
                        throw new Error('Invalid owner id');
                    }

                    const resp = await this.api.getNextUnknownByOwner(ownerId, orderByAsc, startAfterId);

                    return resp.data;
                }
            } catch (error) {
                console.log('error fetching next unknown', error);
                this.showSnackbar({ message: 'Error fetching next response', color: 'error' });
            }
        },
        async bulkAction(action) {
            try {
                const table = this.$refs.tableRef;
                if (this.selectedRows.length === 0) {
                    throw new Error('No rows selected');
                }

                let fhId = this.selectedRows[0]?.funeralHomeId;

                if (!fhId) {
                    throw new Error('Invalid fh id');
                }
                const ids = this.selectedRows.map(r => r.id);

                let status;

                switch (action) {
                    case 'approve':
                        status = GUESTBOOK_RESPONSE_STATUSES.SAFE;
                        break;
                    case 'deny':
                        status = GUESTBOOK_RESPONSE_STATUSES.OFFENSIVE;
                        break;
                    default:
                        throw new Error('Unhandled bulk action');
                }

                var resp = await this.api.updateResponseStatusMulti(ids, fhId, status);
                // clear selection
                table.selection = [];

                this.refetchTableData();
                this.showSnackbar({ message: 'Response approved' });
            } catch (error) {
                console.log(error, 'error approving response');
                this.showSnackbar({ message: 'Error approving response' });
            }
        },
        async approveItem(item) {
            const status = GUESTBOOK_RESPONSE_STATUSES.SAFE;
            try {
                var resp = await this.api.updateResponseStatus(item.id, status);
                this.showSnackbar({ message: 'Response approved' });
                this.refetchTableData();
                this.nextItem();
            } catch (error) {
                console.log(error, 'error approving response');
                this.showSnackbar({ message: 'Error approving response' });
            }
        },
        async denyItem(item) {
            const status = GUESTBOOK_RESPONSE_STATUSES.OFFENSIVE;
            try {
                var resp = await this.api.updateResponseStatus(item.id, status);
                this.showSnackbar({ message: 'Response updated' });
                this.refetchTableData();
                this.nextItem();
            } catch (error) {
                console.log(error, 'error approving response');
                this.showSnackbar({ message: 'Error updating response' });
            }
        },
        async getServiceSubmissionCutoffDate(serviceId) {
            try {
                if (!serviceId) throw new Error('Invalid serviceId');

                const resp = await this.api.getServiceSubmissionCutoffDate(serviceId);

                if (resp.data) {
                    this.serviceSubmissionCutoffDate = resp.data.submissionCutoffDate;
                }
            } catch (error) {
                console.log(error, 'error fetching submission cutoff date');
            }
        },
        async exportToCSV() {
            if (this.exporting) return;
            
            this.exporting = true;
            try {
                let response;
                
                // Ensure sortBy parameter is included
                const exportArgs = {
                    ...this.fetchArgs,
                    sortBy: this.fetchArgs.sortBy || 'Id',
                    sortAsc: this.fetchArgs.sortAsc !== undefined ? this.fetchArgs.sortAsc : false
                };
                
                if (this.mode === 'funeralHome') {
                    if (!this.funeralHomeId) throw new Error('funeralHomeId is required');
                    response = await this.api.exportResponsesByFuneralHome(this.funeralHomeId, exportArgs);
                } else if (this.mode === 'service') {
                    if (!this.serviceId) throw new Error('serviceId is required');
                    try {
                        response = await this.api.exportResponsesByService(this.serviceId, exportArgs);
                    } catch (error) {
                        if (error.message.includes('funeralHomeId')) {
                            this.showSnackbar({
                                message: 'Could not determine funeral home for this service. Please try again later.',
                                color: 'error'
                            });
                        }
                        throw error;
                    }
                } else if (this.mode === 'owner') {
                    response = await this.api.exportResponsesOwner(exportArgs);
                } else if (this.mode === 'admin') {
                    response = await this.api.exportResponsesAdmin(exportArgs);
                } else {
                    throw new Error('Invalid mode');
                }
                
                // Handle the CSV file download
                const blob = response.data; // Response is already a blob
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;
                a.download = `guestbook-export-${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                
                this.showSnackbar({ message: 'Export successful' });
            } catch (error) {
                console.error('Error exporting data:', error);
                this.showSnackbar({ message: 'Error exporting data', color: 'error' });
            } finally {
                this.exporting = false;
            }
        },
        async setAuthToken() {
            this.token = (await this.$auth.getIdTokenClaims()).__raw;
        },
        async fetchOwners() {
            let params = {
                pageSize: 50,
                sortBy: 'Id',
                sortAsc: true,
            };

            try {
                const resp = await this.api.getOwners(params);

                if (resp.data.owners) {
                    this.owners = resp.data.owners;
                }
            } catch (err) {
                console.log(err, 'Error fetching owners');
            }
        },
    },
    created() {
        this.debouncedRefetchData = debounce(this.refetchTableData, 500);
    },
    async mounted() {
        await this.setAuthToken();
        this.api = GuestbookService(this.token);
        

        if (this.allowModeration) {
            const data = await this.getNextUnknown();

            if (data) {
                this.totalUnknown = data.totalUnknown;
            }
        }

        if (this.mode === 'service' && this.serviceId) {
            this.getServiceSubmissionCutoffDate(this.serviceId);
        }

        if (this.mode === 'admin') {
            this.fetchOwners();
        }
    },
};
</script>

<style scoped lang="scss">
.message-box {
    background-color: $light-gray;
    padding: 12px;
    border: 2px solid $medium-gray;
    border-radius: 4px;
    height: 180px;
    overflow-y: auto;
}
.signer-details {
    display: flex;
    gap: 20px;
    justify-content: flex-start;
}
.message-preview {
    width: min(250px, 60vw);
    text-overflow: ellipsis;
    overflow: hidden;
    margin: 5px 0;
    display: inline-block;
    white-space: nowrap;
}
.contact-name {
    font-weight: 600;
}
.tag,
.selected-count {
    background: darken($light-gray, 8%);
    font-weight: bold;
    padding: 2px 6px;
    border-radius: 6px;
}
.button-grid {
    display: flex;
    gap: 6px;
}
.moderation-status {
    background: darken($light-gray, 12%);
    font-weight: bold;
    padding: 2px 6px;
    border-radius: 1rem;
    &.in-title {
        font-size: 0.8rem;
        padding: 0 9px;
        height: 30px;
    }
    &.unknown {
        background: darken($light-gray, 12%);
    }
    &.safe {
        background: #1cc78e;
        color: white;
    }
    &.spam,
    &.denied,
    &.offensive,
    &.questionable {
        background: #f96060;
        color: white;
    }
}
</style>
